import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { decodeToken } from '../../utils/tokenUtils';
import { Content } from 'antd/es/layout/layout';
import { Permissions } from '@liveline/ll_node_common';
import './style.scss';
import Header from '../../layout/header';
import { Layout, LoadingOverlay } from '../../components/shared';
import Navbar from '../../layout/navbar';
import Footer from '../../layout/footer';
import Login from './login/index';
import logo from '../../assets/dark-theme/icons/ll_full_logo_white_text_xparent_bkgd.svg';
import { SharedObjectProvider } from '../../components/shared/ContextProvider';
import AlertFooterBar from '../../components/shared/alert-footer/AlertFooterBar';
import useWindowDimensions from '../../hooks/useWindowDimensions';

/**
 * General format of the webpage, along with <Outlet /> which directs content.
 * 
 * @returns
 */
const PrivateRoutes = () => {
    const isAuthenticated = useSelector((x) =>
        Boolean(x?.auth?.user),
    );
    const user = useSelector((x) => x?.auth?.user);
    const { width, height } = useWindowDimensions();

    // Portrait orientation means height > width (true mobile view)
    const isPortraitMode = height > width;
    // Small screen detection (tablet or mobile)
    const isSmallScreen = width < 1024;
    const [darkTheme, setdarkTheme] = useState('darkTheme');
    const [visibleSideMenu, setVisibleSideMenu] = useState(true); // True = Nav is collapsed (closed)
    const [mobileCollapse, setMobileCollpse] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const location = useLocation();

    // Locations where the footer is hidden-
    const hideFooterLocations = [
        '/live_historical_data/advanced_controllers',
        '/material_tracking',
        '/general_admin/cell-layout',
        '/live_data/scada1',
    ];

    // Compute whether to show the footer- (Locations and if in portrait mode)-
    const showFooter =
        !hideFooterLocations.includes(location.pathname) &&
        !isPortraitMode;

    const onCollapseMenu = () => {
        // When on the dashboard and its the initial load; keep nav menu closed-
        if (location.pathname === '/' && initialLoad) {
            setInitialLoad(false);
            return;
        }

        // Allow side menu toggle regardless of screen size
        if (!isPortraitMode) {
            setVisibleSideMenu(!visibleSideMenu);
        } else {
            // In portrait mode, toggle mobile collapse
            setMobileCollpse(!mobileCollapse);
        }
    };

    const resizeFun = () => {
        // Use the window dimensions from the hook
        const isDefault = localStorage.getItem('isDefault');

        if (isSmallScreen) {
            if (isDefault !== 'false') {
                if (isPortraitMode) {
                    // Portrait mode (mobile) - collapse side menu and show bottom nav
                    setVisibleSideMenu(true);
                    setMobileCollpse(true);
                } else {
                    // Small screen but landscape - keep side menu but collapsed
                    setVisibleSideMenu(true);
                    setMobileCollpse(false);
                }
            }
        }
        if (
            location.pathname ===
            '/live_historical_data/advanced_controllers'
        ) {
            setMobileCollpse(true); // Hide the left nav menu-
        } else if (location.pathname === '/material_tracking') {
            setVisibleSideMenu(true); // Set the left nav menu to minimized-
            setMobileCollpse(true); // Hide the left nav menu-
        } else {
            // Desktop view - reset mobile collapse, keep side menu state
            setMobileCollpse(false);
            // Don't auto-reset visibleSideMenu to allow user control
        }
    };

    const onToggle = () => {
        localStorage.setItem('isDefault', 'false');
        setMobileCollpse(!mobileCollapse);
    };

    // Reset the initial load and side menu on logging out-
    const setLogout = () => {
        setInitialLoad(true);
        setVisibleSideMenu(true);
    };

    // Listen for window dimension changes-
    useEffect(() => {
        resizeFun();
    }, [width, height]);

    useEffect(() => {
        localStorage.setItem('isDefault', 'true');
        return () => {
            // Cleanup if needed
        };
    }, []);

    // Identify user in Umami analytics (use sub from JWT)
    useEffect(() => {
        if (isAuthenticated && user?.idToken && typeof window.umami !== 'undefined') {
            try {
                const decoded = decodeToken(user.idToken);
                const sub = decoded?.sub;
                //console.log(decoded);
                const customerId = decoded["custom:TenantID"]
                if (sub) {
                    window.umami.identify(sub, { customerId });
                }
            } catch {
                // Ignore decode errors
            }
        }
    }, [isAuthenticated, user?.idToken]);

    // Track route changes for Umami analytics (merge with default props so website ID is included)
    useEffect(() => {
        if (typeof window.umami !== 'undefined') {
            window.umami.track((props) => ({
                ...props,
                url: location.pathname + location.search,
                title: document.title,
            }));
        }
    }, [location.pathname, location.search]);

    // Control the state of the menu & footer by the screen location-
    useEffect(() => {
        if (
            location.pathname ===
            '/live_data/scada2'
        ) {
            document.body.classList.add('no-scroll');
        }
        if (
            location.pathname ===
            '/live_historical_data/advanced_controllers'
        ) {
            setMobileCollpse(true); // Hide the left nav menu-
        }
        if (location.pathname === '/material_tracking') {
            setVisibleSideMenu(true); // Set the left nav menu to minimized-
            setMobileCollpse(true); // Hide the left nav menu-
        }
        return () => {
            document.body.classList.remove('no-scroll');
        };
    }, [location]);

    // When the side Navbar is open/closed - set the localStorage value & create a new window event-
    useEffect(() => {
        localStorage.setItem(
            'visibleSideMenu',
            visibleSideMenu.toString(),
        );
        window.dispatchEvent(new Event('visibleSideMenuChange'));
    }, [visibleSideMenu]);

    return isAuthenticated ? (
        <div className={darkTheme}>
                <Layout>
                    <LoadingOverlay>
                        <Navbar
                            onCollapseMenu={onCollapseMenu}
                            visibleSideMenu={visibleSideMenu}
                            isSmallScreen={isSmallScreen}
                            mobileCollapse={mobileCollapse} // True(show nav) / False(hide nav)-
                            isPortraitMode={isPortraitMode}
                        />

                        <Layout className="site-layout">
                            <SharedObjectProvider>
                                <Header
                                    isSmallScreen={isSmallScreen}
                                    onToggle={onToggle}
                                    setLogout={setLogout} // Reset the Initial Load and Side Nav Menu when logging out-
                                    mobileCollapse={mobileCollapse} // True(show nav) / False(hide nav)-
                                />
                                <Content
                                    style={
                                        showFooter
                                            ? {}
                                            : {
                                                  minHeight:
                                                      'calc(100vh - 96px)',
                                                  maxHeight:
                                                      'calc(100vh - 96px)',
                                              }
                                    }
                                >
                                    <Outlet />
                                </Content>
                                {/* LT-3052: live cell-alerts alarm line — always 36px so
                                    page height budgets can subtract a constant */}
                                <AlertFooterBar />
                                {showFooter ? (
                                    <Footer
                                        onToggle={onToggle}
                                        mobileCollapse={
                                            mobileCollapse
                                        }
                                    />
                                ) : null}
                            </SharedObjectProvider>
                        </Layout>
                    </LoadingOverlay>
                </Layout>
        </div>
    ) : (
        <>
            <div className={darkTheme} style={{ height: '100%' }}>
                <div
                    className="site-layout"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                    }}
                >
                    <div className="logoView">
                        <img
                            src={logo}
                            alt="logo"
                            className="logoIMG"
                        />
                    </div>
                    <Login />
                </div>
            </div>
        </>
    );
};

export default PrivateRoutes;
